import { useState, useEffect, useRef } from 'react';
import { Brain, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';

const ITEMS = [
  {
    impact: 'pos', impactLabel: 'Positive signal',
    tickers: ['MU', 'SNDK', 'WDC'],
    time: '4:12 PM ET',
    summary: '<strong>SK Hynix reports record HBM3E shipments</strong> — beating Q1 estimates by 18%. NAND pricing index up 3.2% week-over-week. Analyst consensus shifting toward storage super-cycle call for H2 2026.',
    sources: [
      { label: 'SK Hynix IR', color: '#E53935' },
      { label: 'DRAMeXchange', color: '#1565C0' },
    ],
  },
  {
    impact: 'neg', impactLabel: 'Risk flag',
    tickers: ['LRCX', 'AMAT'],
    time: '3:58 PM ET',
    summary: '<strong>Dutch export license renewal delayed</strong> — ASML citing "ongoing review" for deepUV tools shipments to two undisclosed Asian customers. LRCX secondary exposure via shared customer base.',
    sources: [
      { label: 'Reuters', color: '#FF6F00' },
      { label: 'ASML filing', color: '#0D47A1' },
    ],
  },
  {
    impact: 'wtch', impactLabel: 'Watch',
    tickers: ['INTC', 'AMD'],
    time: '3:45 PM ET',
    summary: '<strong>Data center GPU demand signals mixed</strong> — Hyperscaler capex guidance for Q3 diverging: Microsoft up 12%, Google flat. AMD MI350X sampling reportedly ahead of schedule.',
    sources: [
      { label: 'MSFT 10-Q', color: '#00695C' },
      { label: 'SemiAnalysis', color: '#6A1B9A' },
    ],
  },
];

export default function IntelBand() {
  const [idx, setIdx]         = useState(0);
  const [fading, setFading]   = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef              = useRef(null);
  const INTERVAL              = 6000;

  function goTo(next) {
    setFading(true);
    setTimeout(() => {
      setIdx(next);
      setProgress(0);
      setFading(false);
    }, 350);
  }

  useEffect(() => {
    const start = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min((elapsed % INTERVAL) / INTERVAL * 100, 100);
      setProgress(pct);
      if (pct >= 99) goTo((idx + 1) % ITEMS.length);
    }, 80);
    return () => clearInterval(timerRef.current);
  }, [idx]);

  const item = ITEMS[idx];

  return (
    <div className="intel-band-section">
      <div className="intel-band-wrap">
        <div className="intel-band-header">
          <div className="intel-band-header-left">
            <div className="intel-band-label"><Brain size={13} />Intelligence Signal</div>
            <div className="intel-live"><span className="live-dot" />Live</div>
          </div>
          <div className="intel-band-controls">
            <span className="intel-band-count">{idx + 1} / {ITEMS.length}</span>
            <button className="ibc-btn" onClick={() => goTo((idx - 1 + ITEMS.length) % ITEMS.length)}>
              <ChevronLeft size={13} />
            </button>
            <button className="ibc-btn" onClick={() => goTo((idx + 1) % ITEMS.length)}>
              <ChevronRight size={13} />
            </button>
          </div>
        </div>

        <div className="intel-card">
          <div className="intel-progress">
            <div className="intel-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="intel-card-body">
            <div className={`intel-main ${fading ? 'fade-out' : 'fade-in'}`}>
              <div className={`intel-impact ${item.impact === 'pos' ? 'ii-pos' : item.impact === 'neg' ? 'ii-neg' : 'ii-wtch'}`}>
                <Brain size={11} />{item.impactLabel}
              </div>
              <div className="intel-tickers">
                {item.tickers.map(t => <span key={t} className="intel-ticker-chip">{t}</span>)}
                <span className="intel-time">{item.time}</span>
              </div>
              <div
                className="intel-summary"
                dangerouslySetInnerHTML={{ __html: item.summary }}
              />
              <div className="intel-sources-row">
                <span className="intel-sources-lbl">SOURCES</span>
                {item.sources.map(s => (
                  <span key={s.label} className="intel-src-pill">
                    <span className="src-favicon" style={{ background: s.color }}>
                      {s.label[0]}
                    </span>
                    {s.label}
                    <ExternalLink size={10} />
                  </span>
                ))}
              </div>
            </div>
            <div className={`intel-side ${fading ? 'fade-out' : 'fade-in'}`}>
              <div className="intel-side-lbl">Related</div>
              {item.sources.map(s => (
                <div key={s.label} className="intel-src-card">
                  <div className="isc-img" style={{ background: s.color }}>
                    <Brain size={18} />
                  </div>
                  <div className="isc-body">
                    <div className="isc-pub">{s.label}</div>
                    <div className="isc-title">{item.impactLabel} — {item.tickers.join(', ')}</div>
                  </div>
                </div>
              ))}
              <div className="intel-dots">
                {ITEMS.map((_, i) => (
                  <div
                    key={i}
                    className={`intel-dot ${i === idx ? 'active' : ''}`}
                    onClick={() => goTo(i)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
